import { useEffect, useState } from 'react'
import { Alert, Button, Card, Form, Input, message, Result, Typography } from 'antd'
import { Link } from 'react-router-dom'
import { getKycStatus, submitBusinessKyc } from '../../services/kyc'
import { getMerchant } from '../../services/merchant'
import { FileUploadItem, FileUrls, KycStatusData } from './KycShared'
import type { Merchant } from '../../types'
import MerchantAgreementConsent from '../../components/legal/MerchantAgreementConsent'
import { ApiError } from '../../services/api'
import type { AgreementSelection } from '../../services/merchantAgreement'

const { Title, Text } = Typography

interface BusinessKycFormValues {
  businessName: string
  legalRepresentativeName: string
  companyAddress: string
  website?: string
}

export default function KybSubmit() {
  const [kycStatus, setKycStatus] = useState<KycStatusData | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [fileUrls, setFileUrls] = useState<FileUrls>({})
  const [merchant, setMerchant] = useState<Merchant | null>(null)
  const [agreementOpen, setAgreementOpen] = useState(false)
  const [pendingValues, setPendingValues] = useState<BusinessKycFormValues>()
  const [agreementRefreshKey, setAgreementRefreshKey] = useState(0)
  const [form] = Form.useForm()

  useEffect(() => {
    Promise.all([getKycStatus(), getMerchant()])
      .then(([statusRes, merchantRes]) => {
        setKycStatus(statusRes.data)
        setMerchant(merchantRes.data)
      })
      .finally(() => setLoading(false))
  }, [])

  const handleFileUploaded = (key: keyof FileUrls, url: string) => {
    setFileUrls((prev) => ({ ...prev, [key]: url || undefined }))
  }

  const requiredFilesUploaded =
    !!fileUrls.legalPersonIdFrontUrl &&
    !!fileUrls.legalPersonIdBackUrl &&
    !!fileUrls.certificateOfIncorporationUrl &&
    !!fileUrls.businessRegistrationUrl &&
    !!fileUrls.shareholderStructureUrl &&
    !!fileUrls.narOrNcc1Url
  const merchantProfileComplete = !!merchant?.website?.trim() && !!merchant?.description?.trim()

  const handleSubmit = (values: BusinessKycFormValues) => {
    if (!merchantProfileComplete) {
      message.error('提交 KYB 前，請先在設定中補全網站和商戶描述')
      return
    }
    if (!requiredFilesUploaded) {
      message.error('請上傳所有必填檔案')
      return
    }

    setPendingValues(values)
    setAgreementOpen(true)
  }

  const handleAgreementConfirm = async (agreementSelection: AgreementSelection) => {
    if (!pendingValues) return

    setSubmitting(true)
    try {
      await submitBusinessKyc({
        businessName: pendingValues.businessName,
        legalRepresentativeName: pendingValues.legalRepresentativeName,
        companyAddress: pendingValues.companyAddress,
        website: pendingValues.website || undefined,
        legalPersonIdFrontUrl: fileUrls.legalPersonIdFrontUrl!,
        legalPersonIdBackUrl: fileUrls.legalPersonIdBackUrl!,
        certificateOfIncorporationUrl: fileUrls.certificateOfIncorporationUrl!,
        businessRegistrationUrl: fileUrls.businessRegistrationUrl!,
        shareholderStructureUrl: fileUrls.shareholderStructureUrl!,
        narOrNcc1Url: fileUrls.narOrNcc1Url!,
        ...agreementSelection,
      })
      message.success('KYB 申請已提交，請等待審核')
      setAgreementOpen(false)
      setPendingValues(undefined)
      getKycStatus().then((res) => setKycStatus(res.data))
    } catch (error: unknown) {
      if (error instanceof ApiError && [40911, 40912, 40913, 40914].includes(error.code ?? 0)) {
        setAgreementRefreshKey((key) => key + 1)
        message.warning('協議或簽署者資料已更新，請重新閱讀並確認')
        return
      }
      message.error(error instanceof Error ? error.message : '提交失敗')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <Card loading style={{ maxWidth: 720 }} />
  }

  if (kycStatus?.status === 'APPROVED') {
    return (
      <div>
        <Title level={4} style={{ marginBottom: 24 }}>KYB 認證</Title>
        <Result status="success" title="KYB 認證已通過" subTitle="企業認證審核通過" />
      </div>
    )
  }

  if (kycStatus?.status === 'PENDING') {
    return (
      <div>
        <Title level={4} style={{ marginBottom: 24 }}>KYB 認證</Title>
        <Result status="info" title="KYB 認證審核中" subTitle="您的 KYB 申請正在審核中，請耐心等待。" />
      </div>
    )
  }

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>KYB 認證</Title>

      {kycStatus?.status === 'REJECTED' && (
        <Alert
          type="error"
          message="KYB 認證被拒絕"
          description={kycStatus.rejectReason}
          style={{ marginBottom: 24 }}
          showIcon
        />
      )}

      {!merchantProfileComplete && (
        <Alert
          type="warning"
          message="提交 KYB 前需補全商戶資訊"
          description="請先在賬戶設定中填寫網站和商戶描述。"
          action={<Link to="/dashboard/settings"><Button size="small">前往設定</Button></Link>}
          style={{ maxWidth: 720, marginBottom: 24 }}
          showIcon
        />
      )}

      <Card style={{ maxWidth: 720 }}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="businessName"
            label="企業名稱"
            rules={[
              { required: true, message: '請輸入企業名稱' },
              { pattern: /^[^\u4e00-\u9fa5]+$/, message: '企業名稱不支援中文' },
            ]}
          >
            <Input placeholder="Enterprise Name" />
          </Form.Item>

          <Form.Item
            name="legalRepresentativeName"
            label="公司法人姓名"
            rules={[{ required: true, message: '請輸入公司法人姓名' }]}
          >
            <Input placeholder="Legal Representative Name" autoComplete="name" />
          </Form.Item>

          <Form.Item
            name="companyAddress"
            label="企業地址"
            rules={[{ required: true, message: '請輸入企業地址' }]}
          >
            <Input placeholder="Company Address" />
          </Form.Item>

          <Form.Item
            name="website"
            label="企業網站（可選）"
            rules={[{ pattern: /^[^\u4e00-\u9fa5]+$/, message: '網站地址不支援中文' }]}
          >
            <Input placeholder="https://your-company.com" />
          </Form.Item>

          <div style={{ margin: '8px 0 16px', borderBottom: '1px solid rgba(99,102,241,0.2)', paddingBottom: 8 }}>
            <Text style={{ color: 'var(--landing-text-secondary)', fontSize: 13 }}>企業認證材料（標 * 為必傳）</Text>
          </div>

          <FileUploadItem
            label="公司法人身份證正面"
            fieldKey="legalPersonIdFrontUrl"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.legalPersonIdFrontUrl}
          />
          <FileUploadItem
            label="公司法人身份證背面"
            fieldKey="legalPersonIdBackUrl"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.legalPersonIdBackUrl}
          />
          <FileUploadItem
            label="公司註冊證明書"
            fieldKey="certificateOfIncorporationUrl"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.certificateOfIncorporationUrl}
          />
          <FileUploadItem
            label="商業登記證"
            fieldKey="businessRegistrationUrl"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.businessRegistrationUrl}
          />
          <FileUploadItem
            label="公司股東架構圖"
            fieldKey="shareholderStructureUrl"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.shareholderStructureUrl}
          />
          <FileUploadItem
            label="NAR1 / NNC1"
            fieldKey="narOrNcc1Url"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.narOrNcc1Url}
          />

          <Form.Item style={{ marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              disabled={!requiredFilesUploaded || !merchantProfileComplete}
              data-testid="kyc-submit-business"
            >
              提交
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <MerchantAgreementConsent
        open={agreementOpen}
        candidateLegalName={pendingValues?.businessName ?? ''}
        authorizedUserName={pendingValues?.legalRepresentativeName ?? ''}
        submissionType="BUSINESS"
        merchantAccountId={merchant?.id ?? ''}
        registeredEmail={merchant?.email ?? ''}
        refreshKey={agreementRefreshKey}
        confirming={submitting}
        onCancel={() => {
          if (submitting) return
          setAgreementOpen(false)
          setPendingValues(undefined)
        }}
        onConfirm={handleAgreementConfirm}
      />
    </div>
  )
}
